export interface LearnTap {
  at: number;
  step: number;
  intervals: { milliseconds: number; beats: number }[];
}

/** Match taps to the score, including rests, uneven notes and the loop boundary. */
export function tapLearnTempo(previous: LearnTap | null, at: number, step: number,
  positions: readonly number[], duration: number, beatsPerLoop: number): { tap: LearnTap; bpm: number | null } {
  const fresh = { tap: { at, step, intervals: [] }, bpm: null };
  if (!previous || !Number.isFinite(duration) || duration <= 0 || beatsPerLoop <= 0) return fresh;
  const from = positions[previous.step];
  const to = positions[step];
  if (from === undefined || to === undefined) return fresh;
  const distance = to > from ? to - from : duration - from + to;
  const beats = distance / duration * beatsPerLoop;
  const milliseconds = at - previous.at;
  const previousBeats = previous.intervals.reduce((sum, interval) => sum + interval.beats, 0);
  const previousMs = previous.intervals.reduce((sum, interval) => sum + interval.milliseconds, 0);
  // A longer written note is not a pause. Scale the timeout by its beat length.
  const resetAfter = previousBeats > 0
    ? Math.max(1500, previousMs / previousBeats * beats * 2.5)
    : Math.max(6000, beats * 3000);
  if (milliseconds <= 0 || !Number.isFinite(milliseconds) || beats <= 0 || milliseconds > resetAfter) return fresh;
  const intervals = [...previous.intervals.slice(-3), { milliseconds, beats }];
  const totalMs = intervals.reduce((sum, interval) => sum + interval.milliseconds, 0);
  const totalBeats = intervals.reduce((sum, interval) => sum + interval.beats, 0);
  return { tap: { at, step, intervals }, bpm: Math.round(60_000 * totalBeats / totalMs) };
}

/** Retain every original option and lane while changing the canonical tempo key. */
export function grooveQueryWithTempo(query: string, tempo: number): string {
  const params = new URLSearchParams(query.startsWith("?") ? query.slice(1) : query);
  for (const key of [...params.keys()]) if (key.toLowerCase() === "tempo") params.delete(key);
  params.set("Tempo", String(tempo));
  return `?${params.toString()}`;
}

/** Pin the opened revision; only this session's successful saves advance it. */
export function createGrooveTempoSaver<T extends { title: string; query: string; version: string | number }>(
  initial: T,
  persist: (input: { title: string; query: string; version: T["version"] }) => Promise<{ groove: T }>,
  onSaved: (snapshot: T) => void,
): (tempo: number) => Promise<void> {
  let snapshot = initial;
  return async tempo => {
    const result = await persist({ title: snapshot.title, query: grooveQueryWithTempo(snapshot.query, tempo), version: snapshot.version });
    snapshot = result.groove;
    onSaved(snapshot);
  };
}
